import {
  HsvaColor,
  hexToHsva,
  hsvaToHex,
  hsvaToRgbaString,
  validHex,
} from '@uiw/color-convert'
import Alpha, { PointerProps } from '@uiw/react-color-alpha'
import EditableInput from '@uiw/react-color-editable-input'
import RGBA from '@uiw/react-color-editable-input-rgba'
import Hue from '@uiw/react-color-hue'
import Saturation from '@uiw/react-color-saturation'
import Swatch, { SwatchPresetColor } from '@uiw/react-color-swatch'
import React, { CSSProperties, useEffect, useState } from 'react'

export const PRESET_COLORS = [
  '#CB311F',
  '#FE793E',
  '#FDCC53',
  '#3366B6',
  '#FFB7A9',
  '#95BBCB',
  '#2C4854',
  '#C5B490',
  '#FFEEBC',
  '#FF9840',
  '#A8B7DE',
  '#8D7949',
  '#23AC8A',
  '#376939',
]

export interface SketchProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'color'> {
  prefixCls?: string
  width?: number
  presetColors?: false | SwatchPresetColor[]
  editableDisable?: boolean
  disableAlpha?: boolean
  onChange: (newColorHex: string) => void
  initialValue?: string | HsvaColor
}

function Bar({ ...props }: PointerProps) {
  return (
    <div
      style={{
        boxShadow: 'rgb(0 0 0 / 60%) 0px 0px 2px',
        width: 4,
        top: 1,
        bottom: 1,
        left: props.left,
        borderRadius: 1,
        position: 'absolute',
        backgroundColor: '#fff',
      }}
    />
  )
}

const BgColorPicker = React.forwardRef<HTMLDivElement, SketchProps>(
  (props, ref) => {
    const {
      prefixCls = 'w-color-sketch',
      className,
      onChange,
      width = 360,
      presetColors = PRESET_COLORS,
      initialValue = '3366B6', // default color.
      editableDisable = true,
      disableAlpha = false,
      style,
      ...other
    } = props
    const [hsva, setHsva] = useState(() => {
      if (typeof initialValue === 'object') {
        return initialValue
      }
      if (validHex(initialValue)) {
        return hexToHsva(initialValue)
      }
      return { h: 0, s: 0, v: 0, a: 1 }
    })

    useEffect(() => {
      setHsva(() => {
        if (typeof initialValue === 'object') {
          return initialValue
        }
        if (validHex(initialValue)) {
          return hexToHsva(initialValue)
        }
        return { h: 0, s: 0, v: 0, a: 1 }
      })
    }, [initialValue])

    const handleChange = (hsv: HsvaColor) => {
      setHsva(hsv)
      const hexCode = hsvaToHex(hsv)

      onChange(hexCode)
    }

    const handleHex = (
      value: string | number,
      // eslint-disable-next-line
      evn: React.ChangeEvent<HTMLInputElement>,
    ) => {
      if (
        typeof value === 'string' &&
        validHex(value) &&
        /(3|6)/.test(String(value.length))
      ) {
        handleChange(hexToHsva(value))
      }
    }
    const handleAlphaChange = (newAlpha: { a: number }) =>
      handleChange({ ...hsva, ...{ a: newAlpha.a } })
    const handleSaturationChange = (newColor: HsvaColor) =>
      handleChange({ ...hsva, ...newColor, a: hsva.a })
    const styleMain = {
      '--sketch-background': 'rgb(255, 255, 255)',
      '--sketch-swatch-box-filter': 'rgb(0 0 0 / 20%) 0px 0px 0px 1px inset',
      '--sketch-swatch-border-top': '1px solid rgb(238, 238, 238)',
      background: 'var(--sketch-background)',
      border: 'none',
      width,
      ...style,
    } as CSSProperties
    const styleAlpha = {
      borderRadius: 2,
      background: hsvaToRgbaString(hsva),
      boxShadow: 'var(--sketch-alpha-box-shadow)',
    } as CSSProperties
    const styleSwatch = {
      borderTop: 'var(--sketch-swatch-border-top)',
      paddingBottom: 10,
      paddingTop: 10,
      display: 'flex',
      alignSelf: 'stretch',
      alignItems: 'flex-start',
    } as CSSProperties
    const styleSwatchRect = {
      marginRight: 8.5,
      width: 17,
      height: 16,
      borderRadius: 3,
    } as CSSProperties
    return (
      <div
        {...other}
        className={`${prefixCls} ${className || ''}`}
        ref={ref}
        style={styleMain}>
        <div style={{ padding: '10px 0' }}>
          <Saturation
            hsva={hsva}
            style={{ width: 'auto', height: 150, borderRadius: 2 }}
            onChange={handleSaturationChange}
          />
          <div style={{ display: 'flex', marginTop: 4 }}>
            <div style={{ flex: 1 }}>
              <Hue
                width="auto"
                height={10}
                hue={hsva.h}
                pointer={Bar}
                innerProps={{
                  style: { marginLeft: 1, marginRight: 5 },
                }}
                onChange={(newHue) => handleChange({ ...hsva, ...newHue })}
              />
              {!disableAlpha && (
                <Alpha
                  width="auto"
                  height={10}
                  hsva={hsva}
                  pointer={Bar}
                  style={{ marginTop: 4 }}
                  innerProps={{
                    style: { marginLeft: 1, marginRight: 5 },
                  }}
                  onChange={handleAlphaChange}
                />
              )}
            </div>
            {!disableAlpha && (
              <Alpha
                width={24}
                height={24}
                hsva={hsva}
                radius={2}
                style={{
                  marginLeft: 4,
                }}
                bgProps={{ style: { background: 'transparent' } }}
                innerProps={{
                  style: styleAlpha,
                }}
                // eslint-disable-next-line
                pointer={() => <React.Fragment />}
              />
            )}
          </div>
        </div>
        {editableDisable && (
          <div style={{ display: 'flex', margin: '0 0 3px 0' }}>
            <EditableInput
              label="Hex"
              value={hsvaToHex(hsva).replace(/^#/, '').toLocaleUpperCase()}
              onChange={(evn, val) => handleHex(val, evn)}
              style={{ minWidth: 58 }}
            />
            <RGBA
              hsva={hsva}
              style={{ marginLeft: 6 }}
              aProps={!disableAlpha ? {} : false}
              onChange={(result) => handleChange(result.hsva)}
            />
          </div>
        )}
        {presetColors && presetColors.length > 0 && (
          <Swatch
            style={styleSwatch}
            colors={presetColors}
            color={hsvaToHex(hsva)}
            onChange={(hsvColor) => handleChange(hsvColor)}
            rectProps={{
              style: styleSwatchRect,
            }}
          />
        )}
      </div>
    )
  },
)

BgColorPicker.displayName = 'BgColorPicker'

export default BgColorPicker
